/**
 * useAutosave Hook
 *
 * Combines change detection across workflow + connection stores with a
 * throttled draft save. The frequency (and whether autosave runs at all) is
 * driven by the user's `draftAutosaveMode` preference, read directly from
 * `useUIPreferencesStore` — see the JSDoc on `enabled` below for why.
 *
 * Based on LogicAppsUX CommandBarV2 autosave pattern.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useDraftModeStore, selectIsDraftMode, selectDraftStatus } from '@/stores/draftModeStore';
import { useUIPreferencesStore, getDraftAutosaveOption } from '@/stores/uiPreferencesStore';
import { useThrottledEffect } from './useThrottledEffect';
import { useDraftSave, type UseDraftSaveOptions } from './useDraftSave';
import { useIsDraftDirty } from './useIsDraftDirty';
import { telemetryService } from '@/services/telemetryService';

export interface UseAutosaveOptions extends UseDraftSaveOptions {
  /**
   * External override to force-disable autosave (e.g. monitoring view or
   * read-only modes). The user's `draftAutosaveMode` preference is read
   * inside the hook and ANDed with this prop — callers cannot accidentally
   * keep autosave on when the user has turned it off. Defaults to `true`
   * (follow the user preference).
   */
  enabled?: boolean;
  /**
   * Override the autosave interval in milliseconds. When omitted, the
   * interval is derived from the user's `draftAutosaveMode` preference.
   */
  interval?: number;
  /** Whether the view is in monitoring/read-only mode. */
  isMonitoringView?: boolean;
}

export interface UseAutosaveResult {
  /** Whether autosave is in progress */
  isAutoSaving: boolean;
  /** Last autosave time */
  lastSavedTime: Date | null;
  /** Last autosave error */
  saveError: string | null;
  /** Whether there are unsaved changes waiting to be autosaved */
  needsSaved: boolean;
}

/**
 * Hook for automatic draft saving with throttled effect
 */
export function useAutosave({
  workflowName,
  enabled: enabledOverride = true,
  interval: intervalOverride,
  isMonitoringView = false,
  onSuccess,
  onError,
}: UseAutosaveOptions): UseAutosaveResult {
  const [needsSaved, setNeedsSaved] = useState(false);

  // Subscribe directly to the user's autosave-mode preference. Reading from
  // the store here (instead of relying on prop drilling) eliminates an entire
  // class of bugs where a stale render of an outer wrapper would keep
  // `enabled=true` after the user toggled the menu to `Off`.
  const draftAutosaveMode = useUIPreferencesStore((s) => s.draftAutosaveMode);
  const { enabled: prefEnabled, intervalMs: prefIntervalMs } =
    getDraftAutosaveOption(draftAutosaveMode);

  // External override AND user preference must both allow.
  const enabled = enabledOverride && prefEnabled;
  const interval = intervalOverride ?? prefIntervalMs;
  
  // Timing ref for autosave duration measurement
  const autosaveStartTimeRef = useRef<number>(0);

  // Bounded retry: re-arm needsSaved a short delay after a transient failure
  // (network blip, expired token) so we don't sit idle until the next user edit.
  // We don't escalate to full exponential backoff — most failures resolve on the
  // next attempt, and the user's next edit will retry anyway.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const RETRY_DELAY_MS = 5000;

  // Tracks whether the hook is still mounted. Save callbacks (onSuccess/onError)
  // can fire after unmount when the flush path triggers a fire-and-forget save
  // during navigation. Without this guard, the error handler would schedule a
  // retry timer or call setState on an unmounted hook.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Get state from stores
  const isDraftMode = useDraftModeStore(selectIsDraftMode);
  const draftStatus = useDraftModeStore(selectDraftStatus);
  const setDraftStatus = useDraftModeStore((state) => state.setDraftStatus);
  const setLastSavedTime = useDraftModeStore((state) => state.setLastSavedTime);
  const setSaveError = useDraftModeStore((state) => state.setSaveError);
  
  // Workflow dirty tracking
  const workflowChangeCount = useWorkflowStore((state) => state.changeCount);

  // Connections dirty tracking
  const connectionsChangeCount = useConnectionStore((state) => state.changeCount);

  // Aggregated dirty state: any artifact dirty = needs save
  const isDirty = useIsDraftDirty();

  // Aggregated change count: sum of all artifact change counts
  // This triggers autosave when ANY artifact changes
  const aggregatedChangeCount = workflowChangeCount + connectionsChangeCount;

  // Use draft save hook
  const {
    save: saveDraft,
    isSaving: isAutoSaving,
    lastSavedTime,
    error: saveError,
  } = useDraftSave({
    workflowName,
    onSuccess: () => {
      const duration_ms = performance.now() - autosaveStartTimeRef.current;
      telemetryService.trackEvent('workflow.autosave.success', undefined, { duration_ms });

      // Cancel any pending retry — we recovered.
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      setDraftStatus('saved');
      setLastSavedTime(new Date());
      setSaveError(null);

      // NB: clearing the workflow/connection store dirty flags is handled
      // inside useDraftSave (so manual saves benefit too). That logic also
      // guards against wiping edits that landed during the in-flight save.

      onSuccess?.();
    },
    onError: (err) => {
      telemetryService.trackEvent('workflow.autosave.failed', { error: err.message });

      // Skip all post-unmount work. The save was fired during navigation; we
      // can't recover state into a hook that no longer exists, and we must not
      // schedule timers that will never be cleared.
      if (!isMountedRef.current) {
        onError?.(err);
        return;
      }

      setDraftStatus('error');
      setSaveError(err.message);

      // Restore the dirty signal that performAutosave optimistically cleared.
      // Without this, a failed save silently drops the pending work until the
      // user makes another edit.
      setNeedsSaved(true);

      // Schedule a single retry so transient failures recover without user input.
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (!isMountedRef.current) return;
        // Bumping needsSaved is harmless if already true; the throttled effect
        // will re-evaluate and attempt the save.
        setNeedsSaved(true);
      }, RETRY_DELAY_MS);

      onError?.(err);
    },
  });

  // Cleanup retry timer on unmount.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // When autosave is disabled mid-session, cancel any pending retry timer
  // so a queued retry can't re-arm `needsSaved` after the user opted out.
  // Also clear the stored "last saved" timestamp + error so the status badge
  // tooltip stops claiming an autosave just happened.
  useEffect(() => {
    if (!enabled) {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      setLastSavedTime(null);
      setSaveError(null);
    }
  }, [enabled, setLastSavedTime, setSaveError]);

  // Track unsaved-changes state regardless of whether autosave will run.
  //
  // `enabled` only controls whether the throttled write loop fires — the
  // store-derived `isDirty` signal and the user-facing `needsSaved` /
  // `'dirty'` indicators are facts about the workflow that downstream UI
  // (badge, beforeunload prompt, manual-save button) consumes regardless of
  // the autosave preference. Skipping this update when `enabled=false` would
  // strand users in `mode: 'off'` without any "unsaved changes" affordance.
  useEffect(() => {
    if (aggregatedChangeCount > 0 && isDirty) {
      setNeedsSaved(true);
      setDraftStatus('dirty');
    }
  }, [aggregatedChangeCount, isDirty, setDraftStatus]);

  // If a save completed but a fresh edit landed (or we're in manual-save
  // mode and there are pending edits), re-arm the pending indicator.
  useEffect(() => {
    if (!isAutoSaving && isDirty) {
      setNeedsSaved(true);
    }
  }, [isAutoSaving, isDirty]);

  // Internal save executor. Marks store status and delegates to saveDraft.
  // Does NOT check `needsSaved` — callers gate that separately (throttled
  // path checks needsSaved; flush path checks store dirty). Always honors
  // `isAutoSaving` to avoid concurrent writes.
  const doSave = useCallback(async () => {
    if (!enabled) return;

    setNeedsSaved(false);
    setDraftStatus('saving');
    autosaveStartTimeRef.current = performance.now();

    try {
      await saveDraft();
    } catch (err) {
      // Detailed error reporting happens in useDraftSave's onError.
      console.error('[useAutosave] Autosave failed:', err);
    }
  }, [enabled, setDraftStatus, saveDraft]);

  // Throttled autosave callback: gated on guard conditions + needsSaved.
  const performAutosave = useCallback(async () => {
    // Don't autosave if disabled, not in draft mode, no changes, already
    // saving, or in monitoring/read-only view.
    if (
      !enabled ||
      !isDraftMode ||
      !needsSaved ||
      isAutoSaving ||
      draftStatus === 'saving' ||
      isMonitoringView
    ) {
      return;
    }
    await doSave();
  }, [
    enabled,
    isDraftMode,
    needsSaved,
    isAutoSaving,
    draftStatus,
    isMonitoringView,
    doSave,
  ]);

  // Throttled autosave effect. When autosave is disabled we pass `Infinity`
  // so `useThrottledEffect` skips scheduling entirely — combined with the
  // `!enabled` short-circuits inside `performAutosave` / `doSave`, this
  // gives three layers of defense. NB: a large finite delay would NOT work
  // here: `setTimeout` clamps delays above 2^31 - 1 back to 1ms in both
  // browsers and Node, which would produce a tight re-fire loop.
  const { reset: resetThrottleWindow } = useThrottledEffect(
    performAutosave,
    [performAutosave],
    enabled ? interval : Infinity,
  );

  // Sync the visible "next save" window with the configured interval on
  // every clean → dirty transition. Without this, a user idling past one
  // throttle window would see the next edit save *immediately* (because
  // the throttle's `lastExecutionTime` ref would have drifted past
  // `interval`), making the inline "Autosaved a few seconds ago" status
  // text disagree with the "Autosave: 5 min" pill. Re-anchoring the
  // window to "now" guarantees the next save fires `interval` ms after
  // the edit run started — matching the user's mental model of
  // "Every 5 minutes" as "wait 5 minutes after my edits before saving".
  const wasDirtyRef = useRef(isDirty);
  useEffect(() => {
    if (!wasDirtyRef.current && isDirty && enabled) {
      resetThrottleWindow();
    }
    wasDirtyRef.current = isDirty;
  }, [isDirty, enabled, resetThrottleWindow]);

  // Keep latest doSave + guard state in refs so unload/unmount handlers can
  // flush without re-binding listeners on every render.
  const doSaveRef = useRef(doSave);
  useEffect(() => {
    doSaveRef.current = doSave;
  }, [doSave]);

  const isAutoSavingRef = useRef(isAutoSaving);
  useEffect(() => {
    isAutoSavingRef.current = isAutoSaving;
  }, [isAutoSaving]);

  const flushGuardRef = useRef({ enabled, isDraftMode, isMonitoringView });
  useEffect(() => {
    flushGuardRef.current = { enabled, isDraftMode, isMonitoringView };
  }, [enabled, isDraftMode, isMonitoringView]);

  // Flush a pending save immediately, bypassing the throttle window AND the
  // `needsSaved` React-state gate. Used by the background save listeners
  // (SPA route change, tab hidden, browser close) so changes made within
  // the last throttle interval still persist.
  //
  // We deliberately do NOT check `needsSaved` here — it can be stale in two
  // ways: (1) optimistically cleared at save start while edits accumulate,
  // and (2) lagging the store on the same render as a setState. Reading
  // dirtiness from the store is the source of truth.
  //
  // Returns true when a save was actually fired. Callers that need to know
  // whether the user's data will be persisted (e.g. the beforeunload prompt)
  // can use this to decide whether to fall back to a confirmation dialog.
  const flushPendingSave = useCallback((): boolean => {
    const g = flushGuardRef.current;
    if (!g.enabled || !g.isDraftMode || g.isMonitoringView) return false;
    // Avoid concurrent saves — the in-flight one's onSuccess preserves dirty
    // state if more edits arrived during it, so the next throttle tick or the
    // unmount-time flush from an outer mount will re-trigger.
    if (isAutoSavingRef.current) return false;
    const wfDirty = useWorkflowStore.getState().isDirty;
    const connDirty = useConnectionStore.getState().isDirty;
    if (!wfDirty && !connDirty) return false;
    // Fire-and-forget: the in-flight fetch will continue even after this
    // component unmounts. We can't await here (sync cleanup / unload handlers).
    void doSaveRef.current();
    return true;
  }, []);

  // Background save protection — flush a pending save on unmount.
  //
  // `enabled` is deliberately NOT a dependency here. If it were, React would
  // run this effect's cleanup every time `enabled` flips false mid-session —
  // e.g. when entering template mode or a monitoring view — and that cleanup
  // fires a save, the exact opposite of pausing autosave. (That is how
  // applying a template started persisting a draft to disk immediately.)
  // `flushPendingSave` is a stable callback and already no-ops when autosave
  // is disabled at flush time, so the previous `if (!enabled) return` guard
  // is unnecessary — the cleanup must run only on a genuine unmount.
  useEffect(() => {
    return () => {
      flushPendingSave();
    };
  }, [flushPendingSave]);

  useEffect(() => {
    if (!enabled) return;
    // `pagehide` fires reliably on mobile and bfcache transitions.
    // `visibilitychange` covers tab switching / minimize.
    const onPageHide = () => flushPendingSave();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPendingSave();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, flushPendingSave]);

  // Unload prompt protection — fires whenever there are unsaved changes,
  // regardless of the autosave-mode preference. This is the ONLY thing
  // standing between a user with `mode: 'off'` + dirty edits and silent
  // data loss when they close the tab or navigate away.
  //
  // Attached only while dirty so the listener doesn't disable the browser's
  // bfcache during normal use (Chromium and Safari both opt out of bfcache
  // whenever a beforeunload listener is present, even if it never prompts).
  const hasUnsavedChanges = isDirty && isDraftMode && !isMonitoringView;
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Re-check at fire time — dirty could have cleared between attach and
      // unload (e.g. a save just succeeded).
      const wfDirty = useWorkflowStore.getState().isDirty;
      const connDirty = useConnectionStore.getState().isDirty;
      if (!wfDirty && !connDirty) return;
      // Best-effort flush. No-ops cleanly when autosave is disabled (the
      // user opted out of background saves; the prompt is their protection).
      flushPendingSave();
      e.preventDefault();
      // Legacy spec requires returnValue to be set for the prompt to show.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasUnsavedChanges, flushPendingSave]);

  return {
    isAutoSaving,
    lastSavedTime,
    saveError,
    needsSaved,
  };
}
